import { useEffect, useRef, useState } from 'react';
import SubTitle from '../../components/template/SubTitle'
import Select from 'react-select'
import ButtonFull from '../../components/template/ButtonFull'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faX, faXmark } from '@fortawesome/free-solid-svg-icons'
import ButtonWrap from '../../components/template/ButtonWrap'
import MainTitle from '../../components/template/MainTitle'
import { Link, useNavigate } from 'react-router-dom'
import LoadExistingMergeRule from './components/LoadExistingMergeRule'
import api from '../../components/utils/api';
import LoadBenchmarkMergeRule from './components/LoadBenchmarkMergeRule';
import LoadSenseMergeRule from './components/LoadSenseMergeRule';

const MergeRuleOptionGenerator = ({ mergeRulePairs, setMergeRulePairs, options, index }) => (
    <div className='grid grid-cols-4 my-2 gap-3'>
        <input
            type="text"
            value={mergeRulePairs[mergeRulePairs.findIndex(pair => pair.id === index)].tagName}
            onChange={(e) => {
                // check if the tag name follows the name rule
                const validation = (text) => /[^a-zA-Z_()]/i.test(text);
                const capitalText = (text) => text[0].toUpperCase() + text.substring(1).toLowerCase()
                if (validation(e.target.value)) {
                    alert("Please enter English characters, underbars, and parentheses only.")
                    return;
                }
                const newTagName = e.target.value ? capitalText(e.target.value) : e.target.value
                // check if there is the same class name
                if (newTagName && mergeRulePairs.map(e => e.tagName).includes(newTagName)) {
                    alert("The tag name should be unique.")
                    return;
                }
                let newMergeRulePairs = mergeRulePairs
                newMergeRulePairs[mergeRulePairs.findIndex(pair => pair.id === index)].tagName = newTagName
                setMergeRulePairs([...newMergeRulePairs])
            }}
            className={`border border-[#CCCCCC] h-[38px] p-3 rounded w-full${mergeRulePairs[mergeRulePairs.findIndex(pair => pair.id === index)].hasFilteredClasses ? ' text-violet-500' : ''}`}
        />
        <div className='col-span-3 flex'>
            <div className='w-full'>
                <Select
                    options={options.map(opt => ({ label: opt, value: opt }))}
                    value={mergeRulePairs[mergeRulePairs.findIndex(pair => pair.id === index)].mergedClasses.map(opt => ({ label: opt, value: opt }))}
                    onChange={(e) => {
                        const newPairs = mergeRulePairs
                        newPairs[mergeRulePairs.findIndex(pair => pair.id === index)].mergedClasses = e.map(opt => opt.value)
                        setMergeRulePairs([...newPairs])
                    }}
                    isMulti
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999, textAlign: 'center' }) }}
                    menuPortalTarget={document.querySelector("body")}
                />
            </div>
            <button
                type="button"
                className="ml-2 w-10 h-10 bg-transparent hover:bg-gray-800 text-gray-700 font-semibold hover:text-white py-2 px-3 border border-gray-300 hover:border-transparent rounded disabled:opacity-25"
                onClick={() => setMergeRulePairs(mergeRulePairs.filter(option => option.id !== index))}
            ><FontAwesomeIcon icon={faX} size="xs" /></button>
        </div>
    </div>
)

const NewMergeRuleModal = ({ generalModel, mergeRules }) => {
    const [mergeRuleName, setMergeRuleName] = useState("")
    const [mergeRulePairs, setMergeRulePairs] = useState([{ id: 0, tagName: "", mergedClasses: [] }])
    const [existMergeRulePairs, setExistMergeRulePairs] = useState([])
    const [options, setOptions] = useState([])
    const [dbVersion, setDbVersion] = useState(null)
    const [loadNotice, setLoadNotice] = useState("")
    const nextID = useRef(1);
    const navigate = useNavigate()

    useEffect(() => {
        const fetchOptions = async () => {
            const response = await api.get(`/api/experiments/${generalModel.experiment_id}`)
            setOptions(Object.keys(response.data.event_classes))
            setDbVersion(response.data.database_summary.name)
        }
        if (generalModel) fetchOptions()
    }, [generalModel])

    useEffect(() => {
        if (existMergeRulePairs.length > 0) {
            setMergeRulePairs(existMergeRulePairs.map(e => {
                const mergedClasses = e.merged_classes.filter(ele => options.includes(ele))
                return { id: e.id, tagName: e.name, mergedClasses, hasFilteredClasses: mergedClasses.length !== e.merged_classes.length }
            }))
            nextID.current = Math.max(...existMergeRulePairs.map(e => e.id)) + 1
        }
    }, [options, existMergeRulePairs])

    const addPairs = (count) => {
        const newPairs = Array.from({ length: count }, (_, i) => ({ id: nextID.current + i, tagName: "", mergedClasses: [] }))
        setMergeRulePairs([...mergeRulePairs, ...newPairs])
        nextID.current += count
    }

    const handleCreate = async () => {
        try {
            const response = await api.post(`/api/general-models/${generalModel.id}/merge-rules`, {
                name: mergeRuleName,
                tags: mergeRulePairs.map(e => ({
                    tag_name: e.tagName,
                    merged_classes: e.mergedClasses,
                })),
            });

            if (response.status === 201) {
                navigate(0);
            } else {
                console.warn('Unexpected response status:', response.status);
            }
        } catch (err) {
            console.error('Failed to create merge rule:', err);
        }
    };

    return (
        <div className='h-full grid grid-rows-9'>
            <div className='my-3'>
                <MainTitle title="New Merge Rule" />
                <div className='flex items-end mt-2'>
                    <LoadExistingMergeRule setExistMergeRulePairs={setExistMergeRulePairs} />
                    <LoadSenseMergeRule setMergeRuleName={setMergeRuleName} setMergeRulePairs={setMergeRulePairs} setExistMergeRulePairs={setExistMergeRulePairs} />
                    <LoadBenchmarkMergeRule setMergeRuleName={setMergeRuleName} setExistMergeRulePairs={setExistMergeRulePairs} setLoadNotice={setLoadNotice} dbVersion={dbVersion} />
                    <Link
                        target='_blank' rel="noopener noreferrer"
                        to={`/all-classes/${generalModel.name}`}
                        className="font-semibold py-2 px-4 border hover:border-transparent rounded disabled:opacity-25 bg-transparent hover:bg-gray-800 text-gray-700 hover:text-white border-gray-300"
                    >View Available Classes</Link>
                </div>
                {loadNotice && <div className="mt-2 bg-violet-50 border border-violet-200 text-violet-700 text-sm rounded px-4 py-2 flex justify-between items-center">
                    <span>{loadNotice}</span>
                    <button
                        onClick={() => setLoadNotice("")}
                        title="Dismiss"
                        className="text-violet-400 hover:text-violet-600"
                    ><FontAwesomeIcon icon={faXmark} /></button>
                </div>}
            </div>
            <div>
                <SubTitle title="Merge Rule Name" />
                <input
                    type="text"
                    value={mergeRuleName || ""}
                    onChange={(e) => {
                        // check if the merge rule name follows the name rule
                        const validation = (text) => /[^a-zA-Z0-9|^_-]/i.test(text);
                        if (validation(e.target.value)) {
                            alert("Please enter English characters, numbers, underbars, and hyphens only.")
                            return;
                        }
                        // check if the merge rule name is duplicated
                        if (mergeRules.includes(e.target.value)) {
                            alert("Merge rule name should be unique.")
                            return;
                        }
                        setMergeRuleName(e.target.value)
                    }}
                    className="border border-[#CCCCCC] h-[38px] p-3 rounded w-full my-2"
                />
            </div>
            <div className='grid grid-cols-4'>
                <div><SubTitle title="Tag" /></div>
                <div className='col-span-3'><SubTitle title="Paired Classes" /></div>
            </div>
            <div className='row-span-5 min-h-[300px] flex flex-col'>
                <div className='flex-1 overflow-auto'>
                    {mergeRulePairs.map((pair, i) => <MergeRuleOptionGenerator
                        mergeRulePairs={mergeRulePairs}
                        setMergeRulePairs={setMergeRulePairs}
                        options={options}
                        index={pair.id}
                        key={i}
                    />)}
                </div>
                <div className='flex gap-2 mt-2'>
                    <div className='flex-1'>
                        <ButtonFull contents="Add New Pair" onClick={() => addPairs(1)} />
                    </div>
                    <div className='w-20'>
                        <ButtonFull contents="+5" onClick={() => addPairs(5)} />
                    </div>
                    <div className='w-20'>
                        <ButtonFull contents="+10" onClick={() => addPairs(10)} />
                    </div>
                </div>
            </div>
            <div className='text-right mt-3'>
                <ButtonWrap contents="Create" onClick={handleCreate} isDisabled={!(mergeRuleName && mergeRulePairs.filter(e => e.tagName && e.mergedClasses.length).length)} />
            </div>
        </div >
    )
}

export default NewMergeRuleModal